import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../services/api';

const BusinessClients = ({ projectMode = false }) => {
  // Contexte du projet si en mode projet
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  const canManage = projectMode ? outletContext?.canManage : true;
  
  // Base URL de l'API selon le mode
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showModal, setShowModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState({
    nom: '', telephone: '', email: '', adresse: '', notes: '', credit_autorise: 0
  });
  const [creditAction, setCreditAction] = useState({ type: 'credit', montant: '', notes: '' });

  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadClients = async () => {
    try {
      const res = await api.get(`${apiBase}/clients`);
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedClient) {
        await api.put(`${apiBase}/clients/${selectedClient.id}`, formData);
      } else {
        await api.post(`${apiBase}/clients`, formData);
      }
      loadClients();
      closeModal();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const handleCreditSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`${apiBase}/clients/${selectedClient.id}/credit`, creditAction);
      loadClients();
      setShowCreditModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    try {
      await api.delete(`${apiBase}/clients/${id}`);
      loadClients();
    } catch (err) {
      console.error(err);
    }
  };

  const openModal = (client = null) => {
    if (client) {
      setSelectedClient(client);
      setFormData({
        nom: client.nom || '',
        telephone: client.telephone || '',
        email: client.email || '',
        adresse: client.adresse || '',
        notes: client.notes || '',
        credit_autorise: client.credit_autorise || 0
      });
    } else {
      setSelectedClient(null);
      setFormData({ nom: '', telephone: '', email: '', adresse: '', notes: '', credit_autorise: 0 });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedClient(null);
  };

  const openCreditModal = (client) => {
    setSelectedClient(client);
    setCreditAction({ type: 'credit', montant: '', notes: '' });
    setShowCreditModal(true);
  };

  const filteredClients = clients.filter(c =>
    c.nom?.toLowerCase().includes(search.toLowerCase()) ||
    c.telephone?.includes(search) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedClients = filteredClients.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">Clients</h1>
          <p className="text-gray-500 text-sm">Gérez vos clients et leurs crédits</p>
        </div>
        {canManage && (
          <button
            onClick={() => openModal()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center justify-center gap-2"
          >
            <span>➕</span> Nouveau client
          </button>
        )}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
          <input
            type="text"
            placeholder="Rechercher un client (nom, téléphone, email)..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-400 focus:outline-none"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Résultat de recherche */}
      {search && (
        <div className="mb-4 text-sm text-gray-500">
          {filteredClients.length} résultat(s) pour "{search}"
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-blue-500 text-xl md:text-2xl mb-1 md:mb-2">👥</div>
          <div className="text-xl md:text-2xl font-bold text-gray-800">{clients.length}</div>
          <div className="text-xs md:text-sm text-gray-500">Total clients</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-orange-500 text-xl md:text-2xl mb-1 md:mb-2">💳</div>
          <div className="text-xl md:text-2xl font-bold text-orange-600">
            {clients.filter(c => c.solde_credit > 0).length}
          </div>
          <div className="text-xs md:text-sm text-gray-500">Avec crédit</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-red-500 text-xl md:text-2xl mb-1 md:mb-2">💰</div>
          <div className="text-lg md:text-2xl font-bold text-red-600">
            <span className="hidden sm:inline">{clients.reduce((sum, c) => sum + (parseFloat(c.solde_credit) || 0), 0).toLocaleString()}</span>
            <span className="sm:hidden">{(clients.reduce((sum, c) => sum + (parseFloat(c.solde_credit) || 0), 0) / 1000).toFixed(0)}k</span>
            <span className="text-xs md:text-sm"> XOF</span>
          </div>
          <div className="text-xs md:text-sm text-gray-500">Total crédits</div>
        </div>
      </div>

      {/* Clients List */}
      {paginatedClients.length > 0 ? (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Client</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Contact</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Crédit autorisé</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Solde crédit</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paginatedClients.map(client => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{client.nom}</div>
                      {client.adresse && <div className="text-xs text-gray-400">{client.adresse}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {client.telephone && <div className="text-sm text-gray-600">📞 {client.telephone}</div>}
                      {client.email && <div className="text-sm text-gray-500">✉️ {client.email}</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-gray-600">{(client.credit_autorise || 0).toLocaleString()} XOF</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold ${client.solde_credit > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                        {(client.solde_credit || 0).toLocaleString()} XOF
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center gap-2">
                        {canManage && (
                          <>
                            <button
                              onClick={() => openCreditModal(client)}
                              className="p-2 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition"
                              title="Gérer crédit"
                            >
                              💳
                            </button>
                            <button
                              onClick={() => openModal(client)}
                              className="p-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition"
                              title="Modifier"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDelete(client.id)}
                              className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                              title="Supprimer"
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {paginatedClients.map(client => (
              <div key={client.id} className="bg-white rounded-xl shadow p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-medium text-gray-800">{client.nom}</div>
                    {client.telephone && <div className="text-sm text-gray-500">📞 {client.telephone}</div>}
                    {client.email && <div className="text-xs text-gray-400 truncate max-w-[180px]">✉️ {client.email}</div>}
                  </div>
                  <span className={`text-sm font-bold ${client.solde_credit > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                    {(client.solde_credit || 0).toLocaleString()} XOF
                  </span>
                </div>
                <div className="flex justify-between items-center border-t pt-3">
                  <div className="text-xs text-gray-500">
                    Crédit autorisé: {(client.credit_autorise || 0).toLocaleString()} XOF
                  </div>
                  {canManage && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => openCreditModal(client)}
                        className="p-2 bg-orange-50 text-orange-600 rounded-lg"
                      >
                        💳
                      </button>
                      <button
                        onClick={() => openModal(client)}
                        className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(client.id)}
                        className="p-2 bg-red-50 text-red-600 rounded-lg"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <span className="text-6xl mb-4 block">👥</span>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {search ? 'Aucun résultat' : 'Aucun client'}
          </h3>
          <p className="text-gray-500 mb-4">
            {search ? `Aucun client ne correspond à "${search}"` : 'Commencez par ajouter vos clients'}
          </p>
          {!search && canManage && (
            <button
              onClick={() => openModal()}
              className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
            >
              Ajouter un client
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-lg ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
          >
            ← Précédent
          </button>
          
          <div className="flex gap-1">
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`w-10 h-10 rounded-lg ${currentPage === i + 1 ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-lg ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
          >
            Suivant →
          </button>
        </div>
      )}

      {/* Modal Client */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-800">
                {selectedClient ? 'Modifier le client' : 'Nouveau client'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <input
                  type="text"
                  value={formData.adresse}
                  onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Crédit autorisé (XOF)</label>
                <input
                  type="number"
                  value={formData.credit_autorise}
                  onChange={(e) => setFormData({...formData, credit_autorise: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={2}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
                >
                  {selectedClient ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Crédit */}
      {showCreditModal && selectedClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-800">Gérer le crédit</h2>
              <p className="text-gray-500">{selectedClient.nom}</p>
              <div className="mt-3 p-3 bg-orange-50 rounded-lg">
                <div className="text-sm text-gray-600">Solde actuel</div>
                <div className="text-2xl font-bold text-orange-600">
                  {(selectedClient.solde_credit || 0).toLocaleString()} XOF
                </div>
              </div>
            </div>
            <form onSubmit={handleCreditSubmit} className="p-6 space-y-4">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setCreditAction({...creditAction, type: 'credit'})}
                  className={`flex-1 py-3 rounded-xl font-medium transition ${
                    creditAction.type === 'credit'
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  ➕ Ajouter crédit
                </button>
                <button
                  type="button"
                  onClick={() => setCreditAction({...creditAction, type: 'remboursement'})}
                  className={`flex-1 py-3 rounded-xl font-medium transition ${
                    creditAction.type === 'remboursement'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  ➖ Remboursement
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Montant (XOF)</label>
                <input
                  type="number"
                  min="1"
                  value={creditAction.montant}
                  onChange={(e) => setCreditAction({...creditAction, montant: e.target.value})}
                  required
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none text-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <input
                  type="text"
                  value={creditAction.notes}
                  onChange={(e) => setCreditAction({...creditAction, notes: e.target.value})}
                  placeholder="Motif..."
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <span className="text-gray-500">Nouveau solde: </span>
                <span className={`font-bold text-lg ${
                  creditAction.type === 'credit'
                    ? 'text-orange-600'
                    : 'text-green-600'
                }`}>
                  {creditAction.type === 'credit'
                    ? (parseFloat(selectedClient.solde_credit || 0) + parseFloat(creditAction.montant || 0)).toLocaleString()
                    : Math.max(0, parseFloat(selectedClient.solde_credit || 0) - parseFloat(creditAction.montant || 0)).toLocaleString()
                  } XOF
                </span>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreditModal(false)}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className={`flex-1 py-2 text-white rounded-xl transition ${
                    creditAction.type === 'credit'
                      ? 'bg-orange-600 hover:bg-orange-700'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  Confirmer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessClients;
